import React, { type ReactNode, StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import { appPath, mergeSentryConfig, sentryDSN } from '@fs/zion-config'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router, useLocation } from '@fs/zion-router'
import { NoticeLoading } from '@fs/zion-icon'
import { GroupsProvider } from '@fs/zion-tree-groups'
import { SelectSiblingPedigreeButton, SelectSiblingPedigreeOverlay } from '@fs/zion-tree-pedigree'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import AppLoading from './components/AppLoading'

const EmptyLoading = (): JSX.Element => <div />

const devEnv = process.env.NODE_ENV === 'development'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = devEnv ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities = devEnv ? import('./dev.activities').then((m) => m.default) : undefined

/* Sentry initialization.
 * Sentry is disabled locally by default (SENTRY_DSN is unset and SENTRY_DISABLED=true in .env).
 * Snow runs locally via setupProxy.js and populates window.SERVER_DATA on every page load, so to enable Sentry in development, run:
 * SENTRY_DSN=<your-dsn-from-fs--config> SENTRY_DISABLED=false npm start
 * Events will log under the "local" environment (TARGET_ENV in .env).
 * Additional debugging is gated conditionally by devEnv.
 */
if (sentryDSN) {
  /* Sample rates and strict filtering are hardcoded because CRA bakes REACT_APP_* env vars at BUILD time via webpack DefinePlugin — they cannot be changed at runtime via server config. To adjust these values, change them here and redeploy. */
  const sentryConfig = mergeSentryConfig({
    attachStacktrace: devEnv,
    debug: devEnv, // Log Sentry SDK diagnostics to the console in development.
    sampleRate: devEnv ? 1 : 0.01, // 0.0–1.0 fraction. 100% of events locally.
    strictFiltering: true, // Enhanced filtering via @fs/zion-config (https://github.com/fs-webdev/zion/pull/13771)
  })

  // eslint-disable-next-line no-console -- dev-locked debugging statement of value to the *browser* console.
  devEnv && console.log('Sentry config:', sentryConfig)

  Sentry.init(sentryConfig)
}

type RootWrapperProps = {
  children: ReactNode
}

const RootWrapper = ({ children }: RootWrapperProps): JSX.Element => {
  const { pathname } = useLocation()
  const rootProps =
    pathname?.startsWith('/private-people') || pathname?.startsWith('/recents')
      ? { footer: { useFatFooter: true } }
      : {
          footer: { hidden: true },
          fullScreen: !pathname?.startsWith('/pedigree/descendancy'),
          helperModeProps: { redirectAfterStopUrl: '/tree/pedigree' },
        }

  return (
    // We position the `main` element inside the App component, since we have a subnav that we want to be outside of it. That same `main` element has an id of skipToMainContent so that the skip link works correctly.
    <Root skipLinkId="skipToMainContent" as="div" analytics {...rootProps}>
      {children}
    </Root>
  )
}

const base = appPath ? new URL(appPath).pathname : ''

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)

  /* https://github.com/fs-webdev/zion/pull/10968 broke us hard.
    It removed most react-router path matching logic, due to it passing route strings we provide here through a URL constructor
    That ends up stripping a lot of the regex-based things you can do with path matching (pipes, etc)
    We want to route most things internally here, just not ?treeId=ABC

    We used to have this:
    handledExternalRoutes={[
        { path: `/tree/pedigree/:pid(${pidRegexStr})`, exact: true },
        '/tree/recents',
        '/tree/pedigree/:view(landscape|portrait|first-ancestor|sibling|fanchart)',
      ]}

      But now most of that is broken (can't use pidRegexStr, or pipes), so we're having to be a bit more basic and hacky here.
    */

  return (
    <Router basename={base} handledExternalRoutes={['/tree/pedigree/:pidOrView([a-zA-Z0-9-]+)', '/tree/recents']}>
      <RootWrapper>
        <GroupsProvider
          LoadingComponent={AppLoading}
          SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
          SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
        >
          <App />
        </GroupsProvider>
      </RootWrapper>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => {
  return (
    <StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} LoadingFallback={EmptyLoading} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
